'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { IBoundingBox } from '../../interfaces/geo';
import {
  GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node,
  GetRegionTopSpecies_topSpeciesNearLocation_edges_node,
} from '../../interfaces/graphql';
import { ITopSpeciesFishingSpots } from '../../views/Place/Place';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { TopSpeciesCard } from '../Cards/TopSpeciesCard/TopSpeciesCard';
import { TopSpeciesLinkCard } from '../Cards/TopSpeciesLinkCard/TopSpeciesLinkCard';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { TopSpeciesCTA } from '../TopSpeciesCTA/TopSpeciesCTA';

const DEFAULT_MAX_VISIBLE_ITEMS = 3;

interface IProps {
  appDownloadModalHeader?: string;
  boundingBox?: IBoundingBox;
  className?: string;
  id?: string;
  clickEventSourceId?: string;
  headingText?: string;
  topSpecies:
    | GetPlaceTopSpecies_entry_topSpeciesSummary_edges_node[]
    | GetRegionTopSpecies_topSpeciesNearLocation_edges_node[];
  topSpeciesFishingSpots?: ITopSpeciesFishingSpots[];
  nrOfVisibleItems?: number;
  hasDownloadCTA?: boolean;
  hasShowAllButton?: boolean;
}

export const TopSpeciesSection = ({
  appDownloadModalHeader = `Download the Fishbrain app to see all species from this area`,
  boundingBox,
  clickEventSourceId,
  headingText = `Top fish species in this area`,
  id = 'top-species-section',
  topSpecies,
  className,
  topSpeciesFishingSpots,
  nrOfVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  hasDownloadCTA = true,
  hasShowAllButton = false,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [visibleTopSpeciesItems, setVisibleTopSpeciesItems] = useState(nrOfVisibleItems);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);
  const { currentPage } = useAnalytics();
  const speciesCount = topSpecies.length;
  const hasMoreSpecies = visibleTopSpeciesItems < speciesCount;
  const showAllSpeciesButton = hasShowAllButton && visibleTopSpeciesItems < speciesCount;
  const showMoreSpeciesButton =
    !hasShowAllButton && (hasMoreSpecies || (hasDownloadCTA && !showDownloadCTA));

  const handleShowAll = () => {
    setVisibleTopSpeciesItems(speciesCount);
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleShowMore = () => {
    if (hasMoreSpecies) {
      // Show more species
      setVisibleTopSpeciesItems(prev => Math.min(prev + nrOfVisibleItems, speciesCount));
    } else if (hasDownloadCTA) {
      // Show the CTA if there are no more species and CTA is enabled
      setShowDownloadCTA(true);
    }
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {topSpecies.map(({ image, _id, displayName }, index) => {
          const imageUrl = image.url || '';
          const imageWidth = image.width || 0;
          const imageHeight = image.height || 0;

          if (topSpeciesFishingSpots === undefined) {
            return (
              <TopSpeciesLinkCard
                key={`species-${_id}`}
                speciesId={_id}
                name={displayName}
                className={`w-full ${index >= visibleTopSpeciesItems ? 'hidden' : ''}`}
                testId={`top-species-card-${_id}`}
                imageUrl={imageUrl}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                clickEventPosition={index}
                clickEventSourceId={clickEventSourceId}
                clickEventSource="TopSpeciesSection"
              />
            );
          }

          return (
            <TopSpeciesCard
              boundingBox={index >= visibleTopSpeciesItems ? undefined : boundingBox}
              key={`species-${_id}`}
              speciesId={_id}
              name={displayName}
              className={`w-full ${index >= visibleTopSpeciesItems ? 'hidden' : ''}`}
              testId={`top-species-card-${_id}`}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={clickEventSourceId}
              clickEventSource="TopSpeciesSection"
              nrOfFishingSpots={topSpeciesFishingSpots[index]?.nrOfWaters}
            />
          );
        })}

        {showMoreSpeciesButton && (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowMore}>
            Show more species
          </Button>
        )}

        {showAllSpeciesButton && (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowAll}>
            Show all species
          </Button>
        )}

        {showDownloadCTA && hasDownloadCTA && (
          <TopSpeciesCTA onCTAClick={handleCTAClick} sourceId={clickEventSourceId} />
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="TopSpeciesSection"
        appDownloadSourceId={clickEventSourceId}
        modalHeader={appDownloadModalHeader}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
